import React, {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormText,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
  Table,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { requestJson } from "../../../helpers/api";
import {
  guardarEmpresaActivaId,
  obtenerEmpresaActivaId,
  obtenerUnidadNegocioActivaId,
  suscribirseCambioEmpresaActiva,
  suscribirseCambioUnidadActiva,
  guardarUnidadNegocioActivaId,
} from "../../../utils/empresa";

type Empresa = {
  id: number;
  nombre: string;
  codigo?: string | null;
};

type UnidadCatalogo = {
  id: number;
  nombre: string;
  descripcion?: string | null;
  simbolo?: string | null;
};

type UnidadNegocioCatalogo = {
  id: number;
  nombre: string;
};

type ImpuestoCatalogo = {
  id: number;
  nombre: string;
  tasa?: number | string | null;
};

type Articulo = {
  id: number;
  empresaId: number;
  empresaNombre?: string | null;
  descripcion: string;
  sku?: string | null;
  tipoItem?: string | null;
  unidadMedidaId?: number | null;
  impuestoId?: number | null;
  esReceta: boolean;
  puedeComprar: boolean;
  puedeVender: boolean;
  activo: boolean;
  unidadNegocioId?: number | null;
  unidadNegocioNombre?: string | null;
  asignacionId?: number;
  asignacionActiva?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

type FormState = {
  empresaId: string;
  descripcion: string;
  sku: string;
  tipoItem: string;
  unidadMedidaId: string;
  impuestoId: string;
  esReceta: boolean;
  puedeComprar: boolean;
  puedeVender: boolean;
};

const crearFormInicial = (): FormState => ({
  empresaId: "",
  descripcion: "",
  sku: "",
  tipoItem: "",
  unidadMedidaId: "",
  impuestoId: "",
  esReceta: false,
  puedeComprar: true,
  puedeVender: true,
});

const CatalogoArticulosPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"form" | "list">("form");
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [unidades, setUnidades] = useState<UnidadCatalogo[]>([]);
  const [unidadesNegocio, setUnidadesNegocio] = useState<UnidadNegocioCatalogo[]>([]);
  const [impuestos, setImpuestos] = useState<ImpuestoCatalogo[]>([]);
  const [cargandoBase, setCargandoBase] = useState(true);
  const [baseError, setBaseError] = useState<string | null>(null);
  const [unidadesNegocioError, setUnidadesNegocioError] = useState<string | null>(null);

  const [articulos, setArticulos] = useState<Articulo[]>([]);
  const [articulosLoading, setArticulosLoading] = useState(false);
  const [articulosError, setArticulosError] = useState<string | null>(null);

  const [formValues, setFormValues] = useState<FormState>(crearFormInicial);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [empresaPreferida, setEmpresaPreferida] = useState<number | null>(() =>
    obtenerEmpresaActivaId(),
  );
  const [unidadActivaId, setUnidadActivaId] = useState<number | null>(() =>
    obtenerUnidadNegocioActivaId(obtenerEmpresaActivaId()),
  );

  const [filtroEmpresaId, setFiltroEmpresaId] = useState<string>("");
  const [filtroUnidadId, setFiltroUnidadId] = useState<number | null | undefined>(
    unidadActivaId ?? null,
  );
  const [filtroTexto, setFiltroTexto] = useState("");
  const [mostrarSoloActivos, setMostrarSoloActivos] = useState(true);

  const [importando, setImportando] = useState(false);
  const [importResultado, setImportResultado] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const empresasMapa = useMemo(() => {
    return empresas.reduce<Record<number, Empresa>>((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});
  }, [empresas]);

  const unidadesMapa = useMemo(() => {
    const mapa = new Map<number, string>();
    unidades.forEach((unidad) =>
      mapa.set(
        unidad.id,
        unidad.simbolo
          ? `${unidad.nombre} (${unidad.simbolo})`
          : unidad.nombre,
      ),
    );
    return mapa;
  }, [unidades]);

  const unidadesNegocioMapa = useMemo(() => {
    const mapa = new Map<number, string>();
    unidadesNegocio.forEach((unidad) => mapa.set(unidad.id, unidad.nombre));
    return mapa;
  }, [unidadesNegocio]);

const empresaActivaNombre = useMemo(() => {
  if (!empresaPreferida) return null;
  const match = empresas.find((empresa) => empresa.id === empresaPreferida);
  if (match) {
    return match.nombre;
  }
  return null;
}, [empresaPreferida, empresas]);

  const unidadActivaLabel = useMemo(() => {
    if (!empresaPreferida) return "Seleccioná una empresa";
    if (unidadActivaId === null || unidadActivaId === undefined) {
      return "Todas las unidades";
    }
    return unidadesNegocioMapa.get(unidadActivaId) ?? `Unidad ${unidadActivaId}`;
  }, [empresaPreferida, unidadActivaId, unidadesNegocioMapa]);

  const formularioDeshabilitado = !empresaPreferida || cargandoBase;
  const unidadSelectValue =
    filtroUnidadId === undefined || filtroUnidadId === null
      ? "all"
      : String(filtroUnidadId);

  useEffect(() => {
    document.title = "Catálogos - Artículos | Saas";
  }, []);

  useEffect(() => {
    const unsubscribe = suscribirseCambioEmpresaActiva((id) => {
      setEmpresaPreferida(id);
      setUnidadActivaId(obtenerUnidadNegocioActivaId(id));
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    const unsubscribe = suscribirseCambioUnidadActiva(({ empresaId, unidadId }) => {
      if (!empresaPreferida || empresaId === empresaPreferida) {
        setUnidadActivaId(unidadId ?? null);
      }
    });
    return unsubscribe;
  }, [empresaPreferida]);

  const cargarCatalogosBase = useCallback(async () => {
    setCargandoBase(true);
    setBaseError(null);
    try {
      const [empresasData, unidadesData, impuestosData] = await Promise.all([
        requestJson<Empresa[]>("/api/saas/empresas"),
        requestJson<UnidadCatalogo[]>("/api/facturacion/catalogos/unidades"),
        requestJson<ImpuestoCatalogo[]>("/api/facturacion/catalogos/impuestos"),
      ]);
      const empresasLista = empresasData ?? [];
      setEmpresas(empresasLista);
      setUnidades(unidadesData ?? []);
      setImpuestos(impuestosData ?? []);
      if (empresasLista.length > 0) {
        const almacenada = obtenerEmpresaActivaId();
        const empresaValida = almacenada && empresasLista.some((e) => e.id === almacenada);
        const preferidaId = empresaValida ? almacenada! : empresasLista[0].id;
        if (!empresaValida) {
          guardarEmpresaActivaId(preferidaId);
        }
        setEmpresaPreferida(preferidaId);
      } else {
        setEmpresaPreferida(null);
        setUnidadActivaId(null);
        guardarEmpresaActivaId(null);
      }
    } catch (error) {
      console.error(error);
      setBaseError("No pudimos cargar los catálogos base. Volvé a intentar.");
    } finally {
      setCargandoBase(false);
    }
  }, []);

  useEffect(() => {
    void cargarCatalogosBase();
  }, [cargarCatalogosBase]);

  useEffect(() => {
    if (!empresaPreferida) {
      setFiltroEmpresaId("");
      setFiltroUnidadId(undefined);
      setFormValues((prev) => ({ ...prev, empresaId: "" }));
      return;
    }
    setFiltroEmpresaId(String(empresaPreferida));
    setFiltroUnidadId(unidadActivaId ?? null);
    setFormValues((prev) => {
      if (editandoId) {
        return prev;
      }
      return { ...prev, empresaId: String(empresaPreferida) };
    });
  }, [empresaPreferida, unidadActivaId, editandoId]);

  useEffect(() => {
    if (empresaPreferida === null) return;
    setFiltroEmpresaId(String(empresaPreferida));
    setFormValues((prev) => {
      if (editandoId) return prev;
      if (prev.empresaId === String(empresaPreferida)) {
        return prev;
      }
      return { ...prev, empresaId: String(empresaPreferida) };
    });
  }, [empresaPreferida, editandoId]);

  useEffect(() => {
    const cargarUnidadesNegocio = async () => {
      if (!empresaPreferida) {
        setUnidadesNegocio([]);
        setUnidadesNegocioError(null);
        return;
      }
      setUnidadesNegocioError(null);
      try {
        const data = await requestJson<UnidadNegocioCatalogo[]>(
          `/api/empresas/${empresaPreferida}/unidades-negocio`,
        );
        setUnidadesNegocio(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error(error);
        setUnidadesNegocio([]);
        setUnidadesNegocioError("No pudimos cargar las unidades de negocio.");
      }
    };
    void cargarUnidadesNegocio();
  }, [empresaPreferida]);

  const cargarArticulos = useCallback(async () => {
    setArticulosLoading(true);
    setArticulosError(null);
    try {
      const params = new URLSearchParams();
      if (!mostrarSoloActivos) {
        params.set("includeInactive", "true");
      }
      if (filtroEmpresaId) {
        params.set("empresaId", filtroEmpresaId);
      }
       if (filtroEmpresaId && filtroUnidadId !== undefined) {
         if (filtroUnidadId === null) {
           params.set("unidadNegocioId", "null");
         } else {
           params.set("unidadNegocioId", String(filtroUnidadId));
         }
       }
      const query = params.toString();
      const data = await requestJson<Articulo[]>(
        `/api/articulos${query ? `?${query}` : ""}`,
      );
      setArticulos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setArticulosError("No pudimos obtener los artículos.");
      setArticulos([]);
    } finally {
      setArticulosLoading(false);
    }
  }, [filtroEmpresaId, filtroUnidadId, mostrarSoloActivos]);

  useEffect(() => {
    void cargarArticulos();
  }, [cargarArticulos]);

  const handleTabChange = (tab: "form" | "list") => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const manejarCambioUnidad = (valor: string) => {
    if (!empresaPreferida) return;
    if (valor === "all") {
      setFiltroUnidadId(null);
      setUnidadActivaId(null);
      guardarUnidadNegocioActivaId(empresaPreferida, null);
    } else {
      const numero = Number(valor);
      if (Number.isNaN(numero)) return;
      setFiltroUnidadId(numero);
      setUnidadActivaId(numero);
      guardarUnidadNegocioActivaId(empresaPreferida, numero);
    }
  };

  const handleFormChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const target = event.target;
    const { name, value } = target;
    const isCheckbox =
      target instanceof HTMLInputElement && target.type === "checkbox";
    const checkedValue = isCheckbox ? target.checked : undefined;
    setFormValues((prev) => ({
      ...prev,
      [name]: isCheckbox ? checkedValue : value,
    }));
  };

  const resetForm = () => {
    setFormValues((prev) => ({
      ...crearFormInicial(),
      empresaId: prev.empresaId,
    }));
    setEditandoId(null);
    setFormError(null);
    setFormSuccess(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    setFormSuccess(null);
    if (!formValues.empresaId) {
      setFormError("Seleccioná la empresa.");
      return;
    }
    if (!formValues.descripcion.trim()) {
      setFormError("La descripción es obligatoria.");
      return;
    }
    setFormSubmitting(true);
    try {
      const unidadContexto =
        filtroEmpresaId && filtroUnidadId !== undefined
          ? filtroUnidadId
          : unidadActivaId ?? null;
      const payload = {
        empresaId: Number(formValues.empresaId),
        descripcion: formValues.descripcion.trim(),
        sku: formValues.sku.trim() || null,
        tipoItem: formValues.tipoItem.trim() || null,
        unidadMedidaId: formValues.unidadMedidaId
          ? Number(formValues.unidadMedidaId)
          : null,
        impuestoId: formValues.impuestoId
          ? Number(formValues.impuestoId)
          : null,
        esReceta: formValues.esReceta,
        puedeComprar: formValues.puedeComprar,
        puedeVender: formValues.puedeVender,
        unidadNegocioId: unidadContexto ?? null,
      };
      if (editandoId) {
        await requestJson(`/api/articulos/${editandoId}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        setFormSuccess("Artículo actualizado correctamente.");
      } else {
        await requestJson("/api/articulos", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        setFormSuccess("Artículo creado correctamente.");
      }
      await cargarArticulos();
      resetForm();
    } catch (error) {
      console.error(error);
      setFormError("No pudimos guardar el artículo. Revisá los datos.");
    } finally {
      setFormSubmitting(false);
    }
  };

  const prepararEdicion = (articulo: Articulo) => {
    setActiveTab("form");
    setEditandoId(articulo.id);
    setFormValues({
      empresaId: String(articulo.empresaId),
      descripcion: articulo.descripcion ?? "",
      sku: articulo.sku ?? "",
      tipoItem: articulo.tipoItem ?? "",
      unidadMedidaId: articulo.unidadMedidaId
        ? String(articulo.unidadMedidaId)
        : "",
      impuestoId: articulo.impuestoId ? String(articulo.impuestoId) : "",
      esReceta: articulo.esReceta,
      puedeComprar: articulo.puedeComprar,
      puedeVender: articulo.puedeVender,
    });
  };

  const prepararDuplicado = (articulo: Articulo) => {
    setActiveTab("form");
    setEditandoId(null);
    setFormValues({
      empresaId: String(articulo.empresaId),
      descripcion: `${articulo.descripcion} (copia)`,
      sku: articulo.sku ?? "",
      tipoItem: articulo.tipoItem ?? "",
      unidadMedidaId: articulo.unidadMedidaId
        ? String(articulo.unidadMedidaId)
        : "",
      impuestoId: articulo.impuestoId ? String(articulo.impuestoId) : "",
      esReceta: articulo.esReceta,
      puedeComprar: articulo.puedeComprar,
      puedeVender: articulo.puedeVender,
    });
  };

  const inactivarArticulo = async (articulo: Articulo) => {
    const confirmado = window.confirm(
      `¿Seguro que querés inactivar el artículo "${articulo.descripcion}"?`,
    );
    if (!confirmado) return;
    try {
      await requestJson(`/api/articulos/${articulo.id}`, {
        method: "DELETE",
      });
      await cargarArticulos();
    } catch (error) {
      console.error(error);
      setArticulosError("No pudimos inactivar el artículo.");
    }
  };

  const empresasPorId = useMemo(() => {
    const mapa = new Map<number, Empresa>();
    empresas.forEach((empresa) => mapa.set(empresa.id, empresa));
    return mapa;
  }, [empresas]);

  const unidadesPorId = useMemo(() => {
    const mapa = new Map<number, string>();
    unidades.forEach((unidad) =>
      mapa.set(
        unidad.id,
        unidad.simbolo
          ? `${unidad.nombre} (${unidad.simbolo})`
          : unidad.nombre,
      ),
    );
    return mapa;
  }, [unidades]);

  const impuestosPorId = useMemo(() => {
    const mapa = new Map<number, string>();
    impuestos.forEach((impuesto) => {
      const tasa =
        typeof impuesto.tasa === "number"
          ? `${impuesto.tasa}%`
          : impuesto.tasa ?? "";
      mapa.set(
        impuesto.id,
        tasa ? `${impuesto.nombre} (${tasa})` : impuesto.nombre,
      );
    });
    return mapa;
  }, [impuestos]);

  const articulosFiltrados = useMemo(() => {
    const texto = filtroTexto.trim().toLowerCase();
    return articulos.filter((articulo) => {
      const coincideTexto =
        !texto ||
        articulo.descripcion.toLowerCase().includes(texto) ||
        (articulo.sku ?? "").toLowerCase().includes(texto) ||
        (articulo.tipoItem ?? "").toLowerCase().includes(texto);
      return coincideTexto;
    });
  }, [articulos, filtroTexto]);

  const exportarExcel = () => {
    const registros = articulosFiltrados.map((art) => ({
      ID: art.id,
      Empresa:
        art.empresaNombre ??
        empresasPorId.get(art.empresaId)?.nombre ??
        art.empresaId,
      UnidadNegocio:
        art.unidadNegocioNombre ??
        (art.unidadNegocioId
          ? unidadesNegocioMapa.get(art.unidadNegocioId) ??
            `Unidad ${art.unidadNegocioId}`
          : "Todas"),
      Descripcion: art.descripcion,
      SKU: art.sku ?? "",
      Tipo: art.tipoItem ?? "",
      Unidad: art.unidadMedidaId
        ? unidadesPorId.get(art.unidadMedidaId) ?? art.unidadMedidaId
        : "",
      Impuesto: art.impuestoId
        ? impuestosPorId.get(art.impuestoId) ?? art.impuestoId
        : "",
      Compra: art.puedeComprar ? "Sí" : "No",
      Venta: art.puedeVender ? "Sí" : "No",
      Receta: art.esReceta ? "Sí" : "No",
      Estado: art.activo ? "Activo" : "Inactivo",
    }));
    const worksheet = XLSX.utils.json_to_sheet(registros);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Articulos");
    XLSX.writeFile(workbook, "articulos_catalogo.xlsx");
  };

  const exportarPdf = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });
    doc.setFontSize(14);
    doc.text("Catálogo de artículos", 40, 40);
    doc.setFontSize(10);
    const startY = 70;
    const colWidth = 780 / 6;
    doc.text("ID", 40, startY);
    doc.text("Descripción", 40 + colWidth, startY);
    doc.text("SKU", 40 + colWidth * 2, startY);
    doc.text("Unidad", 40 + colWidth * 3, startY);
    doc.text("Impuesto", 40 + colWidth * 4, startY);
    doc.text("Estado", 40 + colWidth * 5, startY);

    articulosFiltrados.slice(0, 35).forEach((art, index) => {
      const y = startY + 20 + index * 18;
      doc.text(String(art.id), 40, y);
    doc.text(art.descripcion ?? "", 40 + colWidth, y, { maxWidth: colWidth });
      doc.text(art.sku ?? "-", 40 + colWidth * 2, y, { maxWidth: colWidth });
      doc.text(
        art.unidadNegocioNombre ??
          (art.unidadNegocioId
            ? unidadesNegocioMapa.get(art.unidadNegocioId) ??
              `Unidad ${art.unidadNegocioId}`
            : "Todas"),
        40 + colWidth * 3,
        y,
        { maxWidth: colWidth },
      );
      doc.text(
        art.impuestoId ? impuestosPorId.get(art.impuestoId) ?? "-" : "-",
        40 + colWidth * 4,
        y,
        { maxWidth: colWidth },
      );
      doc.text(art.activo ? "Activo" : "Inactivo", 40 + colWidth * 5, y);
    });
    doc.save("articulos_catalogo.pdf");
  };

  const abrirImportador = () => fileInputRef.current?.click();

  const parseBoolean = (valor: string | undefined) => {
    if (!valor) return false;
    const limpio = valor.trim().toLowerCase();
    return ["1", "true", "si", "sí", "y", "yes"].includes(limpio);
  };

  const procesarImportacion = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const archivo = event.target.files?.[0];
    if (!archivo) return;
    setImportResultado(null);
    setImportando(true);
    try {
      const contenido = await archivo.text();
      const [encabezado, ...lineas] = contenido
        .split(/\r?\n/)
        .map((linea) => linea.trim())
        .filter(Boolean);
      if (!encabezado) {
        throw new Error("Archivo vacío.");
      }
      const columnas = encabezado
        .split(",")
        .map((col) => col.trim().toLowerCase());
      const required = ["descripcion", "empresaid"];
      const faltante = required.some((col) => !columnas.includes(col));
      if (faltante) {
        throw new Error(
          "El archivo debe incluir las columnas descripcion y empresaId.",
        );
      }
      let exitos = 0;
      let errores = 0;
      for (const linea of lineas) {
        const valores = linea.split(",").map((v) => v.trim());
        const registro: Record<string, string> = {};
        columnas.forEach((col, index) => {
          registro[col] = valores[index] ?? "";
        });
        try {
          if (!registro.descripcion || !registro.empresaid) {
            throw new Error("Datos incompletos.");
          }
          const payload = {
            empresaId: Number(registro.empresaid),
            descripcion: registro.descripcion,
            sku: registro.sku || null,
            tipoItem: registro.tipoitem || null,
            unidadMedidaId: registro.unidadmedidaid
              ? Number(registro.unidadmedidaid)
              : null,
            impuestoId: registro.impuestoid
              ? Number(registro.impuestoid)
              : null,
            esReceta: parseBoolean(registro.esreceta),
            puedeComprar: registro.puedecomprar
              ? parseBoolean(registro.puedecomprar)
              : true,
            puedeVender: registro.puedevender
              ? parseBoolean(registro.puedevender)
              : true,
          };
          await requestJson("/api/articulos", {
            method: "POST",
            body: JSON.stringify(payload),
          });
          exitos += 1;
        } catch (importError) {
          console.error(importError);
          errores += 1;
        }
      }
      setImportResultado(
        `Importación finalizada. Creados: ${exitos}. Errores: ${errores}.`,
      );
      await cargarArticulos();
    } catch (error) {
      console.error(error);
      setImportResultado("No pudimos procesar el archivo.");
    } finally {
      setImportando(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Artículos"
          pageTitle="Catálogos"
          description="Creá, editá y organizá todos los artículos del sistema."
        />
        {baseError && <Alert color="danger">{baseError}</Alert>}
        <Card className="mt-3">
          <CardHeader>
            <Nav className="nav-tabs-custom card-header-tabs border-bottom-0">
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classNames({ active: activeTab === "form" })}
                  onClick={() => handleTabChange("form")}
                >
                  Nuevo artículo
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classNames({ active: activeTab === "list" })}
                  onClick={() => handleTabChange("list")}
                >
                  Artículos registrados
                </NavLink>
              </NavItem>
            </Nav>
          </CardHeader>
          <CardBody>
            <TabContent activeTab={activeTab}>
              <TabPane tabId="form">
                {formError && <Alert color="danger">{formError}</Alert>}
                {formSuccess && <Alert color="success">{formSuccess}</Alert>}
                {!empresaPreferida && (
                  <Alert color="warning">
                    Seleccioná una empresa (y unidad de negocio) desde el menú de perfil
                    para poder crear artículos en ese contexto.
                  </Alert>
                )}
                <Form onSubmit={handleSubmit}>
                  <fieldset disabled={formularioDeshabilitado}>
                    <Row className="g-3">
                      <Col md={6}>
                        <Label>Empresa activa</Label>
                        <Input
                          value={empresaActivaNombre ?? "Seleccioná una empresa en el perfil"}
                          disabled
                        />
                        <input type="hidden" name="empresaId" value={formValues.empresaId} />
                      </Col>
                      <Col md={6}>
                        <Label>Unidad de negocio</Label>
                        <Input value={unidadActivaLabel} disabled />
                      </Col>
                      <Col md={12}>
                        <Label>Descripción</Label>
                        <Input
                          name="descripcion"
                          value={formValues.descripcion}
                          onChange={handleFormChange}
                          maxLength={255}
                          placeholder="Nombre del artículo"
                        />
                      </Col>
                    <Col md={4}>
                      <Label>SKU</Label>
                      <Input
                        name="sku"
                        value={formValues.sku}
                        onChange={handleFormChange}
                        maxLength={64}
                        placeholder="Código interno u OEM"
                      />
                    </Col>
                    <Col md={4}>
                      <Label>Tipo</Label>
                      <Input
                        name="tipoItem"
                        value={formValues.tipoItem}
                        onChange={handleFormChange}
                        maxLength={64}
                        placeholder="Producto, insumo, combo..."
                      />
                    </Col>
                    <Col md={4}>
                      <Label>Unidad de medida</Label>
                      <Input
                        type="select"
                        name="unidadMedidaId"
                        value={formValues.unidadMedidaId}
                        onChange={handleFormChange}
                      >
                        <option value="">Seleccioná...</option>
                        {unidades.map((unidad) => (
                          <option key={unidad.id} value={unidad.id}>
                            {unidad.nombre}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={4}>
                      <Label>Impuesto</Label>
                      <Input
                        type="select"
                        name="impuestoId"
                        value={formValues.impuestoId}
                        onChange={handleFormChange}
                      >
                        <option value="">Seleccioná...</option>
                        {impuestos.map((imp) => (
                          <option key={imp.id} value={imp.id}>
                            {imp.nombre}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={8}>
                      <Row className="gy-2">
                        <Col sm={4}>
                          <Label className="d-flex gap-2 align-items-center">
                            <Input
                              type="checkbox"
                              name="puedeVender"
                              checked={formValues.puedeVender}
                              onChange={handleFormChange}
                            />
                            <span>Disponible para venta</span>
                          </Label>
                        </Col>
                        <Col sm={4}>
                          <Label className="d-flex gap-2 align-items-center">
                            <Input
                              type="checkbox"
                              name="puedeComprar"
                              checked={formValues.puedeComprar}
                              onChange={handleFormChange}
                            />
                            <span>Disponible para compra</span>
                          </Label>
                        </Col>
                        <Col sm={4}>
                          <Label className="d-flex gap-2 align-items-center">
                            <Input
                              type="checkbox"
                              name="esReceta"
                              checked={formValues.esReceta}
                              onChange={handleFormChange}
                            />
                            <span>Es receta</span>
                          </Label>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <div className="mt-4 d-flex gap-2">
                    <Button type="submit" color="primary" disabled={formSubmitting}>
                      {formSubmitting
                        ? "Guardando..."
                        : editandoId
                        ? "Actualizar"
                        : "Crear artículo"}
                    </Button>
                    <Button type="button" color="link" onClick={resetForm}>
                      Limpiar
                    </Button>
                  </div>
                  </fieldset>
                </Form>
              </TabPane>
              <TabPane tabId="list">
                <Row className="g-3 align-items-end mb-3">
                  <Col md={4}>
                    <Label>Empresa</Label>
                    <Input value={empresaActivaNombre ?? "Seleccioná en el perfil"} disabled />
                  </Col>
                  <Col md={4}>
                    <Label>Unidad</Label>
                    <Input
                      type="select"
                      value={unidadSelectValue}
                      onChange={(e) => manejarCambioUnidad(e.target.value)}
                      disabled={!empresaPreferida || unidadesNegocio.length === 0}
                    >
                      <option value="all">Todas las unidades</option>
                      {unidadesNegocio.map((unidad) => (
                        <option key={unidad.id} value={unidad.id}>
                          {unidad.nombre}
                        </option>
                      ))}
                    </Input>
                    {unidadesNegocioError && (
                      <FormText color="danger">{unidadesNegocioError}</FormText>
                    )}
                  </Col>
                  <Col md={4}>
                    <Label>Búsqueda</Label>
                    <Input
                      placeholder="Descripción, SKU o tipo..."
                      value={filtroTexto}
                      onChange={(e) => setFiltroTexto(e.target.value)}
                    />
                  </Col>
                  <Col md={4}>
                    <Label className="d-flex gap-2 align-items-center">
                      <Input
                        type="checkbox"
                        checked={mostrarSoloActivos}
                        onChange={(e) => setMostrarSoloActivos(e.target.checked)}
                      />
                      <span>Mostrar solo activos</span>
                    </Label>
                  </Col>
                </Row>
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <Button color="secondary" onClick={exportarExcel} disabled={articulosFiltrados.length === 0}>
                    Descargar Excel
                  </Button>
                  <Button color="secondary" onClick={exportarPdf} disabled={articulosFiltrados.length === 0}>
                    Descargar PDF
                  </Button>
                  <Button color="secondary" onClick={abrirImportador} disabled={importando}>
                    {importando ? "Importando..." : "Importar CSV"}
                  </Button>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={procesarImportacion}
                  />
                </div>
                {importResultado && <Alert color="info">{importResultado}</Alert>}
                {articulosError && <Alert color="danger">{articulosError}</Alert>}
                {articulosLoading ? (
                  <div className="py-5 text-center">
                    <Spinner color="primary" />
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table hover className="align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>ID</th>
                          <th>Descripción</th>
                          <th>Contexto</th>
                          <th>SKU</th>
                          <th>Unidad</th>
                          <th>Impuesto</th>
                          <th>Estado</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {articulosFiltrados.length === 0 && (
                          <tr>
                            <td colSpan={7} className="text-center text-muted py-4">
                              No encontramos artículos con los filtros seleccionados.
                            </td>
                          </tr>
                        )}
                        {articulosFiltrados.map((articulo) => (
                          <tr key={`${articulo.id}-${articulo.asignacionId ?? "default"}`}>
                            <td>{articulo.id}</td>
                            <td>
                              <div className="fw-semibold">{articulo.descripcion}</div>
                              <div className="text-muted small">
                                {articulo.tipoItem ?? ""}
                              </div>
                            </td>
                            <td>
                              <div className="fw-semibold">
                                {empresasPorId.get(articulo.empresaId)?.nombre ??
                                  articulo.empresaId}
                              </div>
                              <div className="text-muted small">
                                {articulo.unidadNegocioId
                                  ? `Unidad ${articulo.unidadNegocioId}`
                                  : "Todas las unidades"}
                              </div>
                            </td>
                            <td>{articulo.sku || "—"}</td>
                            <td>
                              {articulo.unidadMedidaId
                                ? unidadesPorId.get(articulo.unidadMedidaId) ?? "—"
                                : "—"}
                            </td>
                            <td>
                              {articulo.impuestoId
                                ? impuestosPorId.get(articulo.impuestoId) ?? "—"
                                : "—"}
                            </td>
                            <td>
                              <Badge color={articulo.activo ? "success" : "secondary"}>
                                {articulo.activo ? "Activo" : "Inactivo"}
                              </Badge>
                            </td>
                            <td>
                              <UncontrolledDropdown>
                                <DropdownToggle color="light" size="sm">
                                  Acciones
                                </DropdownToggle>
                                <DropdownMenu>
                                  <DropdownItem onClick={() => prepararEdicion(articulo)}>
                                    Editar
                                  </DropdownItem>
                                  <DropdownItem onClick={() => prepararDuplicado(articulo)}>
                                    Duplicar
                                  </DropdownItem>
                                  <DropdownItem
                                    onClick={() => inactivarArticulo(articulo)}
                                    disabled={!articulo.activo}
                                  >
                                    Inactivar
                                  </DropdownItem>
                                </DropdownMenu>
                              </UncontrolledDropdown>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default CatalogoArticulosPage;
